import { useState } from 'react'
import { FlowMeterSettings } from './charts/FlowMeterChart'
import { SensorProduct, UnitCategoryId } from '../common/enums'
import { determinePrecision, formatNumber, formatReadingTimestamp } from '../common/utils'
import { useSensorConfigs } from '../hooks/useSensorConfigs'
import { UNIT_CONVERSIONS } from '../common/unitConversions'

const DEFAULT_FIELD_SIZE = 100

interface Props {
  sigfoxId: string
  sensorPort: string
  sensorProduct: SensorProduct
  // Volume unit abbreviations resolved by the chart, so the header always
  // labels the same units the series was plotted in. Null when the port
  // reported no volume-bearing readings at all.
  volumeAbbreviations: Record<string, string> | null
  currentRate: number
  lastReading: number
  totalVolume: number
  totalVolumeInAcreFoot: number
  settings: FlowMeterSettings
}

export function BadgerSDIChartSubHeader({
  sigfoxId,
  sensorPort,
  sensorProduct,
  volumeAbbreviations,
  totalVolume,
  totalVolumeInAcreFoot,
  currentRate,
  lastReading,
  settings,
}: Props) {
  const [fieldSize, setFieldSize] = useState(DEFAULT_FIELD_SIZE)

  // Fetched through React Query rather than a useEffect so it is keyed and
  // deduplicated. As a raw effect this refired on every remount, and the chart
  // list above remounts whenever the page re-renders while the device list is
  // still streaming in — which meant one page load issued this same request
  // ~18 times. `enabled` also stops the hook defaulting the timestamp to
  // Date.now(), which would change the query key on every render.
  const { data: sensorConfigResponse } = useSensorConfigs(sigfoxId, lastReading, false, !!sigfoxId && !!lastReading)

  const { conversionScale, conversionOffset } = (sensorConfigResponse as any)?.data?.[sensorPort] ?? {}

  const volumeRateConversions = {
    ...UNIT_CONVERSIONS[UnitCategoryId.VolumeRate],
    current: {
      65: 'cubic meters / second',
      formula: '@',
    },
  }

  const ricSensorInfo = `${sensorPort}: ${sensorProduct}`

  function calculateWaterDepth() {
    if (fieldSize === 0 || isNaN(fieldSize)) {
      return '-'
    }

    const waterDepthInInches = (totalVolumeInAcreFoot / fieldSize) * 12
    let precision = determinePrecision(waterDepthInInches, 3)
    precision = precision < settings.volume.precision ? settings.volume.precision : precision
    return formatNumber(waterDepthInInches, precision) + ' in'
  }

  return (
    <div className={tailwind.chartSubHeader}>
      <table className={tailwind.table}>
        <tbody>
          <tr className={tailwind.topTableRow}>
            <td>{ricSensorInfo}</td>
            <td>{formatReadingTimestamp(lastReading)}</td>
            <td>{`Rate: ${formatNumber(currentRate, settings.volumeRate.precision)} ${
              volumeRateConversions === null
                ? 'acre-ft'
                : volumeRateConversions.abbreviations[settings.volumeRate.unitId]
            }`}</td>
            <td>
              <div className="flex flex-row">
                {`Totals: ${formatNumber(totalVolume, settings.volume.precision)} ${
                  volumeAbbreviations?.[settings.volume.unitId] ?? ''
                }`}
              </div>
            </td>
          </tr>
          <tr className="text-sm">
            <td>{`K-Value: ${conversionScale ?? ''}`}</td>
            <td>{`Diameter: ${conversionOffset ?? ''}`}</td>
            <td className="pr-10">
              <div className="flex flex-row">
                <input
                  className={tailwind.inputText}
                  type="number"
                  id="field-size"
                  placeholder={'field size'}
                  value={fieldSize}
                  onChange={(e) => {
                    let value = parseInt(e.target.value)
                    if (value < 0) {
                      value = 0
                    }
                    setFieldSize(value)
                  }}
                />
                <div className="pt-0.5 pl-3">acres</div>
              </div>
            </td>
            <td>
              <div className="flex flex-row">{`Depth: ${calculateWaterDepth()}`}</div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

const tailwind = {
  chartSubHeader: 'text-black text-l font-regular bg-[#DDD] pl-4 pr-4 w-full',

  table: 'table-fixed w-full',

  topTableRow: 'h-8 text-sm',

  inputText:
    'appearance-none border border-red-500 rounded w-full pt-0.5 px-1 mb-1 text-sm text-gray-700 leading-tight focus:outline-none cursor-pointer w-20',

  inputDropdown:
    'appearance-none border border-red-500 rounded w-full my-2 mr-3 py-2 px-3 text-sm text-gray-700 leading-tight focus:outline-none cursor-pointer',

  lockIcon: 'w-10 pt-2 pl-3 cursor-pointer',
}
